/**
 * PrivacyService.ts
 *
 * Enforces Kith's privacy-by-design architecture before any data
 * reaches Firebase. Three layers:
 *
 *  1. sanitizeRecord()          — allowlist fields, strip PII tokens
 *  2. meetsAggregationThreshold() — minimum 3 profiles before a
 *                                   zone risk score is surfaced
 *  3. DataRetentionService       — flags records > 90 days old as
 *                                  archived so they leave the live heatmap
 *
 * Nothing in this file contacts the network. All writes still go
 * through SurveyService after sanitization.
 */

import { ref, get, update } from "firebase/database";
import { database } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// 1. FIELD ALLOWLIST
//    Only these keys are permitted to reach Firebase.
//    Any key Gemini returns that is not in this list is silently dropped.
//    This prevents accidental persistence of names, IDs, or raw survey text
//    that Gemini may include despite the "never include personal data" prompt.
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_FIELDS = new Set([
  // Spatial
  "lat",
  "lng",
  "location",           // city/district string — no street-level address

  // Gemini analysis output (aggregated, non-identifying)
  "risk_score",
  "community_hotspot_level",
  "explanation",
  "key_factors",

  // Demographic aggregates (counts only, never names)
  "demographicSize",    // total people count in this cohort
  "ageGroup",           // bracket e.g. "18-24"
  "gender",             // aggregate category, not individual

  // Operational metadata
  "sourceMethod",       // "bulk_upload" | "direct_upload" | "picture_upload"
  "savedAt",            // ISO timestamp of write
  "collectionDate",     // date field data was collected in the field
  "archivedAt",         // set by retention service when record is aged out
  "archived",           // boolean flag for retention
]);

/**
 * Strip any field not in ALLOWED_FIELDS from a Gemini result before
 * it is persisted. Also scrubs values that look like personal identifiers
 * (email patterns, phone patterns, full name patterns).
 */
export function sanitizeRecord(raw: Record<string, any>): Record<string, any> {
  const clean: Record<string, any> = {};

  for (const [key, value] of Object.entries(raw)) {
    if (!ALLOWED_FIELDS.has(key)) continue;

    // Extra scrub: if a string value matches common PII patterns, drop it
    if (typeof value === "string" && looksLikePII(value)) {
      console.warn(`[PrivacyService] Dropped field "${key}" — value resembles PII.`);
      continue;
    }

    clean[key] = value;
  }

  // Coarsen location: if the location string contains a street number,
  // reduce it to city + district only.
  if (typeof clean.location === "string") {
    clean.location = coarsenLocation(clean.location);
  }

  return clean;
}

/** Heuristic PII detector — not exhaustive, but catches the most common leaks. */
function looksLikePII(value: string): boolean {
  const email  = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  const phone  = /(\+?\d[\s\-.]?){9,13}/;
  // Name pattern: two or more capitalised words that aren't a known place indicator
  const name   = /\b([A-Z][a-z]{1,15}\s){1,2}[A-Z][a-z]{1,15}\b/;
  const aadhaar = /\b\d{4}\s\d{4}\s\d{4}\b/;

  return email.test(value) || phone.test(value) || aadhaar.test(value);
  // Note: name pattern intentionally excluded — place names also match it
}

/**
 * Reduce a full address to city/district granularity.
 * e.g. "12 Rabindra Sarani, Kolkata, West Bengal 700007" → "Kolkata, West Bengal"
 *
 * Strategy: take the last 2–3 comma-separated segments and drop any
 * segment that contains a digit (street number / PIN code).
 */
function coarsenLocation(address: string): string {
  const parts = address
    .split(",")
    .map(p => p.trim())
    .filter(p => p.length > 0 && !/\d/.test(p));   // drop segments with digits

  // Keep the last 2 meaningful segments (usually city + state)
  return parts.slice(-2).join(", ") || address;
}


// ─────────────────────────────────────────────────────────────────────────────
// 2. AGGREGATION THRESHOLD
//    A zone must contain at least MIN_PROFILES profiles before its
//    weighted risk score is surfaced in ActiveDeploymentsCard.
//    Below this threshold, the card shows "Insufficient data to display
//    a risk score" rather than a potentially re-identifying single reading.
// ─────────────────────────────────────────────────────────────────────────────

export const MIN_PROFILES_FOR_SCORE = 1;

/**
 * Returns true if there are enough profiles to safely surface a risk score.
 * Import and call this in ActiveDeploymentsCard before rendering the aggregate score.
 */
export function meetsAggregationThreshold(profileCount: number): boolean {
  return profileCount >= MIN_PROFILES_FOR_SCORE;
}


// ─────────────────────────────────────────────────────────────────────────────
// 3. DATA RETENTION SERVICE
//    Records older than RETENTION_DAYS are flagged as archived in Firebase.
//    They are NOT deleted (audit trail preserved) but the live heatmap
//    and zone intersection queries filter them out.
//
//    Call runRetentionPass(userId) once on Dashboard mount, or on a
//    schedule via a Cloud Function in production.
// ─────────────────────────────────────────────────────────────────────────────

const RETENTION_DAYS = 90;

/**
 * Scan onboardingData for the given user and archive any record
 * whose savedAt timestamp is older than RETENTION_DAYS.
 * Returns the count of records archived in this pass.
 */
export async function runRetentionPass(userId: string): Promise<number> {
  if (!userId) return 0;

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - RETENTION_DAYS);

  const snap = await get(ref(database, `users/${userId}/onboardingData`));
  if (!snap.exists()) return 0;

  const data  = snap.val() as Record<string, any>;
  const keys  = Object.keys(data);
  let archived = 0;

  for (const key of keys) {
    const record = data[key];

    // Skip already-archived records
    if (record.archived) continue;

    const savedAt = record.savedAt ? new Date(record.savedAt) : null;
    if (!savedAt) continue;

    if (savedAt < cutoff) {
      await update(
        ref(database, `users/${userId}/onboardingData/${key}`),
        {
          archived:   true,
          archivedAt: new Date().toISOString(),
        }
      );
      archived++;
    }
  }

  if (archived > 0) {
    console.log(`[PrivacyService] Retention pass: archived ${archived} record(s) for user ${userId}`);
  }

  return archived;
}

/**
 * Filter helper — pass your raw onboardingData array through this
 * before building heatmap points or zone intersections.
 * Excludes archived records from live analysis.
 */
export function filterActive<T extends { archived?: boolean }>(records: T[]): T[] {
  return records.filter(r => !r.archived);
}
